import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Users, Plus, Shield, Loader2, AlertCircle,
  Key, ToggleLeft, ToggleRight, Check, X,
} from "lucide-react";
import { AUTH_API, getPermissionsCatalog } from "../../../api/auth";

// Utilisé uniquement si le catalogue backend (permissions/catalog/) n'a
// pas pu être chargé — source de vérité normale = groups renvoyés par
// l'API, partagée avec teqa_confirmation.
const FALLBACK_PERMISSION_GROUPS = [
  { key: "dashboard", label: "Dashboard", permissions: [
    { code: "dashboard.view", label: "View dashboard" },
    { code: "dashboard.view_own", label: "View own dashboard" },
  ]},
  { key: "orders", label: "Orders", permissions: [
    { code: "orders.view", label: "View orders" },
    { code: "orders.assign", label: "Assign orders" },
    { code: "orders.update_status", label: "Update status" },
    { code: "orders.view_stats", label: "View statistics" },
  ]},
  { key: "offers", label: "Offers", permissions: [
    { code: "offers.view", label: "View offers" },
    { code: "offers.view_marketplace", label: "View marketplace" },
    { code: "offers.create", label: "Create" },
    { code: "offers.update_own", label: "Update own" },
    { code: "offers.delete_own", label: "Delete own" },
    { code: "offers.change_status", label: "Change status" },
  ]},
  { key: "collaborations", label: "Collaborations", permissions: [
    { code: "collaborations.view", label: "View" },
    { code: "collaborations.view_own", label: "View own" },
    { code: "collaborations.create", label: "Create" },
    { code: "collaborations.respond", label: "Respond" },
    { code: "collaborations.manage", label: "Manage" },
  ]},
  { key: "wallets", label: "Wallets", permissions: [
    { code: "wallets.view_own", label: "View own wallet" },
    { code: "wallet_transfers.view", label: "View transfers" },
    { code: "wallet_transactions.view", label: "View transactions" },
  ]},
  { key: "commissions", label: "Commissions", permissions: [
    { code: "commissions.view_own", label: "View own" },
    { code: "commissions.view_stats", label: "View statistics" },
  ]},
  { key: "invoices", label: "Invoices", permissions: [
    { code: "invoices.view_own", label: "View own" },
    { code: "invoices.download", label: "Download" },
  ]},
  { key: "messaging", label: "Messaging", permissions: [
    { code: "messaging.view_own", label: "View conversations" },
    { code: "messaging.send", label: "Send messages" },
    { code: "messaging.mark_read", label: "Mark as read" },
  ]},
  { key: "profile", label: "Profile & Settings", permissions: [
    { code: "profile.update", label: "Update profile" },
    { code: "settings.update", label: "Update settings" },
  ]},
  { key: "team", label: "Team", permissions: [
    { code: "team.view", label: "View team" },
    { code: "team.create", label: "Create members" },
    { code: "team.update", label: "Update members" },
    { code: "team.manage_permissions", label: "Manage permissions" },
  ]},
];

function PermissionsModal({ user, onClose, onSave, saving, groups }) {
  const permissionGroups = groups?.length ? groups : FALLBACK_PERMISSION_GROUPS;
  const isFullAccess = user.permissions?.includes("*");
  const [fullAccess, setFullAccess] = useState(isFullAccess);
  const [selected, setSelected] = useState(() =>
    isFullAccess ? [] : (user.permissions || [])
  );

  const togglePerm = (code) =>
    setSelected((prev) =>
      prev.includes(code) ? prev.filter((p) => p !== code) : [...prev, code]
    );

  return (
    <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Edit Permissions</h2>
            <p className="text-sm text-slate-500 dark:text-slate-500 mt-0.5">
              {user.first_name} {user.last_name} — {user.email}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 dark:text-slate-500"
          >
            <X size={18} />
          </button>
        </div>

        {/* Full access toggle */}
        <div className="px-6 pt-5">
          <div
            className={`flex items-center justify-between p-4 rounded-xl cursor-pointer border transition-colors
              ${fullAccess ? "bg-indigo-50 dark:bg-indigo-500/10 border-indigo-200 dark:border-indigo-500/30" : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"}`}
            onClick={() => setFullAccess((v) => !v)}
          >
            <div>
              <p className="font-semibold text-sm text-slate-800 dark:text-slate-200">Full Access</p>
              <p className="text-xs text-slate-500 dark:text-slate-500 mt-0.5">
                Grant complete access — same as the store owner
              </p>
            </div>
            <div
              className={`w-11 h-6 rounded-full transition-colors ${
                fullAccess ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-700"
              }`}
            >
              <div
                className={`w-5 h-5 bg-white rounded-full shadow mt-0.5 transition-transform ${
                  fullAccess ? "translate-x-5 ml-0.5" : "ml-0.5"
                }`}
              />
            </div>
          </div>
        </div>

        {/* Permission groups */}
        {!fullAccess && (
          <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">
            {permissionGroups.map((group) => (
              <div key={group.key}>
                <p className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">
                  {group.label}
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {group.permissions.map((perm) => {
                    const checked = selected.includes(perm.code);
                    return (
                      <label
                        key={perm.code}
                        className={`flex items-center gap-2.5 p-2.5 rounded-xl cursor-pointer border text-sm transition-colors
                          ${checked
                            ? "bg-indigo-50 dark:bg-indigo-500/10 border-indigo-200 dark:border-indigo-500/30 text-indigo-700 dark:text-indigo-400"
                            : "bg-slate-50 dark:bg-slate-800 border-transparent text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                          }`}
                      >
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={checked}
                          onChange={() => togglePerm(perm.code)}
                        />
                        <div
                          className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${
                            checked ? "bg-indigo-600" : "border-2 border-slate-300 dark:border-slate-600"
                          }`}
                        >
                          {checked && <Check size={10} className="text-white" strokeWidth={3} />}
                        </div>
                        {perm.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-slate-100 dark:border-slate-800">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(fullAccess ? ["*"] : selected)}
            disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Shield size={15} />
            )}
            Save Permissions
          </button>
        </div>
      </div>
    </div>
  );
}

function getInitials(u) {
  const fn = u.first_name?.[0] || "";
  const ln = u.last_name?.[0] || "";
  return (fn + ln).toUpperCase() || u.username?.[0]?.toUpperCase() || "?";
}

const TeamUsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [savingPerms, setSavingPerms] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [permissionGroups, setPermissionGroups] = useState([]);

  useEffect(() => {
    getPermissionsCatalog()
      .then((res) => setPermissionGroups(res.data?.groups || []))
      .catch(() => setPermissionGroups([]));
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await AUTH_API.get("user/children/");
      setUsers(res.data.results || res.data);
    } catch (err) {
      const msg = err.response?.data?.error || "Could not load team members.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleToggleStatus = async (u) => {
    const newActive = !u.is_active;
    const newStatus = newActive ? "ACTIVE" : "INACTIVE";
    try {
      setTogglingId(u.id);
      await AUTH_API.patch(`user/children/${u.id}/status/`, {
        is_active: newActive,
        status: newStatus,
      });
      setUsers((prev) =>
        prev.map((m) =>
          m.id === u.id ? { ...m, is_active: newActive, status: newStatus } : m
        )
      );
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to update status.";
      alert(msg);
    } finally {
      setTogglingId(null);
    }
  };

  const handleSavePermissions = async (permissions) => {
    try {
      setSavingPerms(true);
      await AUTH_API.patch(`user/children/${editingUser.id}/status/`, { permissions });
      setUsers((prev) =>
        prev.map((m) => (m.id === editingUser.id ? { ...m, permissions } : m))
      );
      setEditingUser(null);
    } catch (err) {
      const msg = err.response?.data?.error
        || (err.response?.data && Object.values(err.response.data)?.[0]?.[0])
        || "Failed to update permissions.";
      alert(msg);
    } finally {
      setSavingPerms(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Team Members</h1>
          <p className="text-sm text-slate-500 dark:text-slate-500 mt-0.5">
            Manage sub-accounts and their permissions
          </p>
        </div>
        <Link
          to="/users/add"
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 shadow-sm"
        >
          <Plus size={16} />
          Add Member
        </Link>
      </div>

      {/* Content */}
      {loading ? (
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 p-12 flex items-center justify-center">
          <Loader2 size={24} className="animate-spin text-indigo-600 dark:text-indigo-400" />
        </div>
      ) : error ? (
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 p-8">
          <div className="flex items-center gap-3 text-red-600 dark:text-rose-400">
            <AlertCircle size={20} />
            <p className="text-sm font-medium">{error}</p>
          </div>
        </div>
      ) : users.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 p-12 text-center">
          <div className="w-14 h-14 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users size={24} className="text-slate-400 dark:text-slate-500" />
          </div>
          <p className="text-slate-700 dark:text-slate-300 font-medium">No team members yet</p>
          <p className="text-sm text-slate-500 dark:text-slate-500 mt-1 mb-5">
            Add your first employee to get started
          </p>
          <Link
            to="/users/add"
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700"
          >
            <Plus size={15} />
            Add Member
          </Link>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800">
                  <th className="text-left px-6 py-3 font-medium text-slate-500 dark:text-slate-400">Member</th>
                  <th className="text-left px-6 py-3 font-medium text-slate-500 dark:text-slate-400">Status</th>
                  <th className="text-left px-6 py-3 font-medium text-slate-500 dark:text-slate-400">Permissions</th>
                  <th className="text-left px-6 py-3 font-medium text-slate-500 dark:text-slate-400">Added</th>
                  <th className="text-right px-6 py-3 font-medium text-slate-500 dark:text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-indigo-100 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-xl flex items-center justify-center font-semibold text-xs shrink-0">
                          {getInitials(u)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-slate-100">
                            {u.first_name} {u.last_name}
                          </p>
                          <p className="text-xs text-slate-400 dark:text-slate-500">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          u.status === "ACTIVE"
                            ? "bg-green-50 dark:bg-emerald-500/10 text-green-700 dark:text-emerald-400"
                            : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-500"
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            u.status === "ACTIVE" ? "bg-green-500" : "bg-slate-400"
                          }`}
                        />
                        {u.status === "ACTIVE" ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {u.permissions?.includes("*") ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400">
                          <Shield size={11} />
                          Full Access
                        </span>
                      ) : (
                        <span className="text-slate-500 dark:text-slate-500 text-xs">
                          {u.permissions?.length || 0} permission
                          {u.permissions?.length !== 1 ? "s" : ""}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-500 dark:text-slate-500 text-xs">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditingUser(u)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                        >
                          <Key size={13} />
                          Permissions
                        </button>
                        <button
                          onClick={() => handleToggleStatus(u)}
                          disabled={togglingId === u.id}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                            u.is_active
                              ? "bg-red-50 dark:bg-rose-500/10 text-red-600 dark:text-rose-400 hover:bg-red-100 dark:hover:bg-rose-500/20"
                              : "bg-green-50 dark:bg-emerald-500/10 text-green-700 dark:text-emerald-400 hover:bg-green-100 dark:hover:bg-emerald-500/20"
                          }`}
                        >
                          {togglingId === u.id ? (
                            <Loader2 size={13} className="animate-spin" />
                          ) : u.is_active ? (
                            <ToggleRight size={13} />
                          ) : (
                            <ToggleLeft size={13} />
                          )}
                          {u.is_active ? "Disable" : "Enable"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editingUser && (
        <PermissionsModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleSavePermissions}
          saving={savingPerms}
          groups={permissionGroups}
        />
      )}
    </div>
  );
};

export default TeamUsersList;
